import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Descriptions,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Select,
  InputNumber,
  Spin,
  Popconfirm,
  Empty,
} from 'antd';
import {
  ArrowRightOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { companiesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import { jalaliValueToApi } from '../../utils/jalaliApiDate';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Option } = Select;

const PACKAGE_TYPE_LABEL = {
  monthly: 'ماهانه',
  annual: 'سالانه',
  lifetime: 'مادام‌العمر',
};

const STATUS_LABEL = {
  active: 'فعال',
  inactive: 'غیرفعال',
  license_expired: 'منقضی مجوز',
};

const isTrialPackage = (pkg) => String(pkg?.name || '').toLowerCase().trim() === 'trial';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/companies', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [pkgModalOpen, setPkgModalOpen] = useState(false);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgSubmitting, setPkgSubmitting] = useState(false);
  const [pkgForm] = Form.useForm();
  const [pkgMeta, setPkgMeta] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companiesApi.show(id);
      const companyRow = res?.company ?? res?.data?.company;
      setData({
        company: companyRow,
        latest_invoice: res?.latest_invoice ?? res?.data?.latest_invoice,
        invoice_currency: res?.invoice_currency ?? res?.data?.invoice_currency,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات شرکت');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const company = data?.company;

  const openPackageModal = async () => {
    setPkgModalOpen(true);
    setPkgLoading(true);
    setPkgMeta(null);
    pkgForm.resetFields();
    try {
      const res = await companiesApi.getEditPackageMeta(id);
      const inner = res?.data ?? res;
      setPkgMeta(inner);
      const c = inner?.company;
      pkgForm.setFieldsValue({
        package: c?.package_id,
        package_type: c?.package_type || 'annual',
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت بسته‌ها');
    } finally {
      setPkgLoading(false);
    }
  };

  const packagesList = pkgMeta?.packages || [];
  const watchedPackage = Form.useWatch('package', pkgForm);
  const selectedPkg = useMemo(() => packagesList.find((p) => Number(p.id) === Number(watchedPackage)), [packagesList, watchedPackage]);
  const trialSelected = isTrialPackage(selectedPkg);

  const submitPackage = async () => {
    const values = await pkgForm.validateFields();
    const payload = {
      package: values.package,
      package_type: values.package_type,
    };
    if (trialSelected) {
      payload.trial_expire_on = jalaliValueToApi(values.trial_expire_on);
    } else {
      payload.pay_date = jalaliValueToApi(values.pay_date);
      payload.next_pay_date = jalaliValueToApi(values.next_pay_date);
      payload.licence_expire_on = jalaliValueToApi(values.licence_expire_on);
      if (values.amount != null && values.amount !== '') payload.amount = values.amount;
    }
    setPkgSubmitting(true);
    try {
      await companiesApi.updatePackage(id, payload);
      message.success('بسته بروزرسانی شد');
      setPkgModalOpen(false);
      reload();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, pkgForm);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setPkgSubmitting(false);
    }
  };

  const onDelete = async () => {
    try {
      await companiesApi.delete(id);
      message.success('شرکت حذف شد');
      goBackToList();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font py-24 text-center">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!company) {
    return (
      <MainLayout>
        <div className="custom-font p-6">
          <Empty description="شرکت یافت نشد" />
          <Button className="mt-4" onClick={goBackToList}>
            بازگشت
          </Button>
        </div>
      </MainLayout>
    );
  }

  const inv = data?.latest_invoice;

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0 max-w-6xl mx-auto space-y-4">
        <Card
          className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 shadow-sm"
          styles={{ body: { padding: '16px 20px' } }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/25 flex items-center justify-center flex-shrink-0">
                {company.logo_url ? (
                  <img src={company.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <BankOutlined className="text-xl text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold m-0 truncate">{company.company_name}</h1>
                <div className="mt-1 flex flex-wrap gap-2 items-center">
                  <Tag color={company.status === 'active' ? 'green' : company.status === 'license_expired' ? 'orange' : 'default'}>
                    {STATUS_LABEL[company.status] || company.status}
                  </Tag>
                  {company.package?.name ? (
                    <Tag icon={<AppstoreOutlined />} color="blue">
                      {company.package.name} — {PACKAGE_TYPE_LABEL[company.package_type] || company.package_type}
                    </Tag>
                  ) : null}
                </div>
              </div>
            </div>
            <Space wrap className="flex-shrink-0">
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
                لیست
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/settings/companies/${id}/edit`, {
                    state: { returnTo: returnToRef.current, ...buildGoBackState() },
                  })
                }
              >
                ویرایش اطلاعات
              </Button>
              <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} onClick={openPackageModal}>
                ویرایش بسته
              </Button>
              <Popconfirm title="حذف شرکت؟" okText="حذف" cancelText="لغو" onConfirm={onDelete}>
                <Button danger icon={<DeleteOutlined />}>
                  حذف
                </Button>
              </Popconfirm>
            </Space>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="جزئیات">
            <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="ایمیل شرکت" span={2}>
                {company.company_email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="تلفن">{company.company_phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="وب‌سایت">{company.website || '—'}</Descriptions.Item>
              <Descriptions.Item label="منطقه زمانی">{company.timezone || '—'}</Descriptions.Item>
              <Descriptions.Item label="ارز">
                {company.currency ? `${company.currency.currency_symbol} ${company.currency.currency_code}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="انقضای مجوز" span={2}>
                {company.licence_expire_on ? String(company.licence_expire_on).replace('T', ' ') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="آدرس" span={2}>
                {company.address || company.company_address?.[0]?.location || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="آمار">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <div className="text-gray-500 text-xs">کارمندان</div>
                <div className="text-lg font-semibold">{company.employees_count ?? '—'}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <div className="text-gray-500 text-xs">مشتریان</div>
                <div className="text-lg font-semibold">{company.clients_count ?? '—'}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <div className="text-gray-500 text-xs">پروژه‌ها</div>
                <div className="text-lg font-semibold">{company.projects_count ?? '—'}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <div className="text-gray-500 text-xs">آخرین ورود</div>
                <div className="text-xs font-medium mt-1">{company.last_login ? String(company.last_login).replace('T', ' ') : '—'}</div>
              </div>
            </div>
            {company.user ? (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs text-gray-500 mb-1">مدیر شرکت</div>
                <div className="font-medium">{company.user.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400" dir="ltr">
                  {company.user.email}
                </div>
              </div>
            ) : null}
          </Card>
        </div>

        {inv ? (
          <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="آخرین فاکتور اشتراک">
            <Descriptions bordered column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label="شماره">{inv.invoice_number ?? inv.id}</Descriptions.Item>
              <Descriptions.Item label="نوع بسته">{PACKAGE_TYPE_LABEL[inv.package_type] || inv.package_type}</Descriptions.Item>
              <Descriptions.Item label="مبلغ">{inv.total != null ? inv.total : '—'}</Descriptions.Item>
              <Descriptions.Item label="تاریخ پرداخت">{inv.pay_date ? String(inv.pay_date).replace('T', ' ') : '—'}</Descriptions.Item>
              <Descriptions.Item label="پرداخت بعدی">{inv.next_pay_date ? String(inv.next_pay_date).replace('T', ' ') : '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        <Modal
          title="ویرایش بسته شرکت"
          open={pkgModalOpen}
          onCancel={() => setPkgModalOpen(false)}
          onOk={submitPackage}
          confirmLoading={pkgSubmitting}
          okText="ذخیره"
          cancelText="لغو"
          width={560}
          destroyOnClose
          className="custom-font"
        >
          {pkgLoading ? (
            <div className="py-10 text-center">
              <Spin />
            </div>
          ) : (
            <Form form={pkgForm} layout="vertical" className="mt-2">
              <Form.Item name="package" label="بسته" rules={[{ required: true, message: 'بسته را انتخاب کنید' }]}>
                <Select showSearch optionFilterProp="children" popupClassName="custom-font" placeholder="بسته">
                  {packagesList.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="package_type" label="نوع اشتراک" rules={[{ required: true }]}>
                <Select popupClassName="custom-font">
                  <Option value="monthly">ماهانه</Option>
                  <Option value="annual">سالانه</Option>
                  <Option value="lifetime">مادام‌العمر</Option>
                </Select>
              </Form.Item>
              {trialSelected ? (
                <Form.Item name="trial_expire_on" label="تاریخ انقضای آزمایشی" rules={[{ required: true, message: 'تاریخ را انتخاب کنید' }]}>
                  <PersianDatePicker placeholder="انتخاب تاریخ" />
                </Form.Item>
              ) : (
                <>
                  <Form.Item name="pay_date" label="تاریخ پرداخت">
                    <PersianDatePicker placeholder="انتخاب تاریخ" />
                  </Form.Item>
                  <Form.Item name="next_pay_date" label="پرداخت بعدی">
                    <PersianDatePicker placeholder="انتخاب تاریخ" />
                  </Form.Item>
                  <Form.Item name="licence_expire_on" label="انقضای مجوز">
                    <PersianDatePicker placeholder="انتخاب تاریخ" />
                  </Form.Item>
                  <Form.Item name="amount" label="مبلغ">
                    <InputNumber className="w-full" min={0} />
                  </Form.Item>
                </>
              )}
            </Form>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CompanyDetail;
